import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { Datagrid, FunctionField, Link, List, useLocaleState, useRecordContext, useTranslate } from "react-admin";

import { ROWS_PER_PAGE } from "../constants";
import { formatDateTimeInline, formatTimeOnDay } from "../lib/dateUtils";
import { formatName } from "../lib/formatName";
import { getProcedureDocumentation, getProcedureDocumentationSyncs } from "../lib/procedureDocumentation";
import { procedureShowPath } from "../lib/procedureRoutes";
import { space } from "../theme/designTokens";
import { PatientRecord } from "../types/patient";
import { ProcedureRecord } from "../types/procedure";
import { DocumentationButtonField } from "./fields/DocumentationButtonField";
import { DocumentationSyncStatus } from "./fields/DocumentationSyncStatus";
import { ProcedureStatusChip } from "./fields/ProcedureStatusChip";
import { InfoDialog } from "./InfoDialog";

// Narrow screens keep this much of the Maßnahme name visible.
const PROCEDURE_NAME_MIN_WIDTH = 240;

// The Maßnahme name takes the remaining width; the other columns only what they need.
// With the default cell padding the other columns leave the name no room.
const datagridSx = {
  "& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root": {
    paddingLeft: `${space.md}px`,
    paddingRight: `${space.md}px`,
  },
  "& .column-codeDisplay": { width: "100%" },
  "& .MuiTableBody-root .column-codeDisplay": { maxWidth: 0, minWidth: PROCEDURE_NAME_MIN_WIDTH },
};

const OtherPatientsField = ({ currentPatientId }: { currentPatientId: string }) => {
  const procedureRecord = useRecordContext<ProcedureRecord>();

  if (!procedureRecord) return null;

  // Get all participating patients
  const allPatients =
    procedureRecord.procedureParticipants?.nodes
      ?.map(participant => participant.patient)
      .filter((patient): patient is NonNullable<typeof patient> => patient !== null && patient !== undefined) || [];

  // Filter out the current patient (the one whose detail page we're on)
  const otherPatients = allPatients.filter(patient => patient.id !== currentPatientId);

  if (otherPatients.length === 0) {
    return <span>-</span>;
  }

  if (otherPatients.length === 1) {
    const patient = otherPatients[0];
    const patientName = formatName(patient.user);
    return <Link to={`/Patient/${patient.id}/show`}>{patientName}</Link>;
  }

  // For multiple other patients, show the first one with a count
  const firstPatient = otherPatients[0];
  const firstPatientName = formatName(firstPatient.user);

  return (
    <Link to={`/Patient/${firstPatient.id}/show`}>
      {firstPatientName} (+{otherPatients.length - 1} more)
    </Link>
  );
};

export const PatientProcedureList = () => {
  const record = useRecordContext<PatientRecord>();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [modalState, setModalState] = useState<{ title: string; content: string } | null>();

  if (!record) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("procedureList.noDataAvailable")}
      </Typography>
    );
  }

  return (
    <List
      resource="Procedure"
      filter={{ "patient.id": record.id }}
      actions={false}
      title={translate("procedureList.title")}
      empty={<Typography variant="body1">{translate("procedureList.noProcedures")}</Typography>}
      perPage={ROWS_PER_PAGE}
      sort={{ field: "plannedStart", order: "DESC" }}
    >
      <Datagrid bulkActionButtons={false} rowClick={id => procedureShowPath(String(id), record.id)} sx={datagridSx}>
        <ProcedureStatusChip />
        <FunctionField
          label={translate("Procedure.fields.plannedStart")}
          render={(procedure: ProcedureRecord) => formatDateTimeInline(procedure.plannedStart, locale)}
        />
        <FunctionField
          source="codeDisplay"
          label={translate("Procedure.fields.codeDisplay")}
          sortable={false}
          render={(procedure: ProcedureRecord) => (
            <Box
              component="span"
              title={procedure.codeDisplay ?? undefined}
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {procedure.codeDisplay}
            </Box>
          )}
        />
        <FunctionField
          label={translate("Procedure.fields.otherPatients")}
          render={() => <OtherPatientsField currentPatientId={record.id} />}
        />
        <FunctionField
          label={translate("Procedure.fields.performedStart")}
          render={(procedure: ProcedureRecord) =>
            formatTimeOnDay(procedure.performedStart, procedure.plannedStart, locale)
          }
        />
        <FunctionField
          label={translate("Procedure.fields.performedEnd")}
          render={(procedure: ProcedureRecord) =>
            formatTimeOnDay(procedure.performedEnd, procedure.plannedStart, locale)
          }
        />
        <FunctionField
          label={translate("Procedure.fields.performer")}
          render={(procedure: ProcedureRecord) => formatName(procedure.performer?.user)}
        />
        <DocumentationButtonField
          label={translate("Procedure.fields.documentation")}
          getContent={procedure => getProcedureDocumentation(procedure, record.userId)}
          onDocumentationClick={(title, content) => setModalState({ title, content })}
        />
        <FunctionField
          label={translate("Procedure.fields.documentationSync")}
          render={(procedure: ProcedureRecord) => (
            <DocumentationSyncStatus syncs={getProcedureDocumentationSyncs(procedure, record.userId)} />
          )}
        />
      </Datagrid>
      {modalState && (
        <InfoDialog title={modalState.title} content={modalState.content} onClose={() => setModalState(null)} />
      )}
    </List>
  );
};
